import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert, Box, Button, ButtonGroup, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle,
  Grid, Link, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField,
  Tooltip as MuiTooltip, Typography, useTheme,
} from '@mui/material';
import { Bar } from 'react-chartjs-2';
import { BarElement, CategoryScale, Chart as ChartJS, LinearScale, Tooltip } from 'chart.js';
import { addDays, format, parseISO } from 'date-fns';
import { useSelector } from 'react-redux';
import DispatchService from '../../setting/dispatch/DispatchService';

ChartJS.register(CategoryScale, LinearScale, BarElement, Tooltip);

// "สรุปคนกด" (owner 2026-11-03): one page to see, per day, who the bot sent links to, who made it,
// how fast, and the yen bought. Clicking a day narrows the buyer table to that day. Days are
// service days (02:00 → 02:00), same as the bot's daily counts. Same shop scope as the Mercari pages.

/** Today's service day in Thailand, whatever the browser's clock: 00:00–01:59 Thai still belongs to yesterday. */
const serviceToday = () => parseISO(new Date(Date.now() - 2 * 3600 * 1000).toLocaleDateString('en-CA', { timeZone: 'Asia/Bangkok' }));
const thaiTime = (iso) => new Date(iso).toLocaleTimeString('th-TH', { timeZone: 'Asia/Bangkok', hour: '2-digit', minute: '2-digit' });
const MAX_DAYS = 92; // the backend's limit, both ends counted
const ymd = (d) => format(d, 'yyyy-MM-dd');
const RANGES = [
  { label: 'วันนี้', back: 0, days: 1 },
  { label: 'เมื่อวาน', back: 1, days: 1 },
  { label: '7 วัน', back: 0, days: 7 },
  { label: '30 วัน', back: 0, days: 30 },
];
const rangeOf = ({ back, days }) => {
  const to = addDays(serviceToday(), -back);
  return { from: ymd(addDays(to, -(days - 1))), to: ymd(to) };
};

const yen = (n) => `¥${Number(n || 0).toLocaleString()}`;
const duration = (sec) => {
  if (sec == null) return '—';
  if (sec < 60) return `${sec} วิ`;
  const min = Math.round(sec / 60);
  if (min < 60) return `${min} นาที`;
  return `${Math.floor(min / 60)} ชม. ${min % 60} นาที`;
};
const dayLabel = (date) => format(parseISO(date), 'd MMM');

const Stat = ({ label, value, sub, color }) => (
  <Paper variant="outlined" sx={{ p: 2, height: '100%' }}>
    <Typography variant="body2" color="text.secondary">{label}</Typography>
    <Typography variant="h5" fontWeight={700} color={color}>{value}</Typography>
    {sub && <Typography variant="caption" color="text.secondary">{sub}</Typography>}
  </Paper>
);

const Head = ({ title, hint }) => (
  <TableCell align="right">
    {hint ? <MuiTooltip title={hint}><span style={{ borderBottom: '1px dotted' }}>{title}</span></MuiTooltip> : title}
  </TableCell>
);

const BuyerTable = ({ buyers, onPick }) => (
  <TableContainer component={Paper} variant="outlined">
    <Table size="small">
      <TableHead>
        <TableRow>
          <TableCell>คนกด</TableCell>
          <Head title="ได้ลิงก์" hint="จำนวนการ์ดที่บอทส่งให้" />
          <Head title="กดทัน" hint="ส่งหลักฐานสำเร็จ รวมที่แอดมินปิดงานให้" />
          <Head title="ไม่ทัน" hint="เปิดลิงก์แล้วไม่ได้ของ หรือกดไม่ได้/ของหมด" />
          <Head title="ไม่ตอบ" hint="หมดเวลาโดยไม่เปิดลิงก์" />
          <Head title="% กดทัน" hint="กดทัน ÷ (กดทัน + ไม่ทัน + ไม่ตอบ) ไม่นับงานที่ยังไม่จบ" />
          <Head title="เปิดลิงก์เฉลี่ย" hint="จากส่งการ์ด ถึงเปิดลิงก์ครั้งแรก" />
          <Head title="ส่งหลักฐานเฉลี่ย" hint="จากส่งการ์ด ถึงส่งหลักฐาน อาจนานกว่าเวลากดจริง เพราะบางคนซื้อก่อนแล้วค่อยส่งรูป" />
          <TableCell align="right">ยอดกด</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {buyers.length === 0 && (
          <TableRow><TableCell colSpan={9}><Typography variant="body2" color="text.secondary">ไม่มีข้อมูลในช่วงนี้</Typography></TableCell></TableRow>
        )}
        {buyers.map((b) => (
          <TableRow key={b.id} hover sx={{ cursor: 'pointer' }} onClick={() => onPick(b)}>
            <TableCell>
              {b.label}
              {b.pending > 0 && <Chip size="small" variant="outlined" label={`ค้าง ${b.pending}`} sx={{ ml: 0.5 }} />}
              {b.double > 0 && <Chip size="small" color="warning" label={`ซื้อซ้ำ ${b.double}`} sx={{ ml: 0.5 }} />}
              {b.failed > 0 && <Chip size="small" color="error" variant="outlined" label={`ส่งการ์ดไม่สำเร็จ ${b.failed}`} sx={{ ml: 0.5 }} />}
            </TableCell>
            <TableCell align="right">{b.cards}</TableCell>
            <TableCell align="right">
              {b.won + b.adminWon}
              {b.adminWon > 0 && <Typography variant="caption" color="text.secondary"> (แอดมินปิด {b.adminWon})</Typography>}
            </TableCell>
            <TableCell align="right">{b.late}</TableCell>
            <TableCell align="right">{b.noReply}</TableCell>
            <TableCell align="right">{b.winRate == null ? '—' : `${b.winRate}%`}</TableCell>
            <TableCell align="right">{duration(b.avgOpenSec)}</TableCell>
            <TableCell align="right">{duration(b.avgProofSec)}</TableCell>
            <TableCell align="right">{yen(b.jpy)}</TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  </TableContainer>
);

const OUTCOME = {
  won: { label: 'กดทัน', color: 'success' },
  adminWon: { label: 'แอดมินปิดให้', color: 'success' },
  double: { label: 'ซื้อซ้ำ', color: 'warning' },
  late: { label: 'ไม่ทัน', color: 'default' },
  noReply: { label: 'ไม่ตอบ', color: 'default' },
  pending: { label: 'ยังไม่จบ', color: 'info' },
  failed: { label: 'ส่งการ์ดไม่สำเร็จ', color: 'error' },
  void: { label: 'ไม่นับ', color: 'default' },
};
const whenThai = (iso) => (iso ? new Date(iso).toLocaleString('th-TH', {
  timeZone: 'Asia/Bangkok', day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit',
}) : '—');

/** Click a buyer in แยกตามคนกด (owner 2026-11-04): their summary and every card in the range. */
const BuyerDetail = ({ buyer, range, shop, onClose }) => {
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  useEffect(() => {
    let live = true;
    setData(null);
    setError('');
    DispatchService.getReportBuyer(buyer.id, { ...range, shop })
      .then((res) => { if (live) setData(res.data); })
      .catch((err) => { if (live) setError(err?.response?.data?.message || 'โหลดข้อมูลไม่สำเร็จ'); });
    return () => { live = false; };
  }, [buyer.id, range.from, range.to, shop]); // eslint-disable-line react-hooks/exhaustive-deps -- range is rebuilt each render for a picked day
  const b = data?.buyer || buyer;
  const rangeText = range.from === range.to ? dayLabel(range.from) : `${dayLabel(range.from)} – ${dayLabel(range.to)}`;
  return (
    <Dialog open onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>
        {b.label}
        <Typography component="span" variant="body2" color="text.secondary"> · {rangeText}</Typography>
      </DialogTitle>
      <DialogContent dividers>
        <Grid container spacing={2} mb={2}>
          <Grid item xs={6} sm={3}><Stat label="ได้ลิงก์" value={b.cards ?? '—'} /></Grid>
          <Grid item xs={6} sm={3}><Stat label="กดทัน" value={b.won != null ? b.won + (b.adminWon || 0) : '—'} sub={b.winRate != null ? `${b.winRate}% · ไม่ทัน ${b.late} · ไม่ตอบ ${b.noReply}` : null} /></Grid>
          <Grid item xs={6} sm={3}><Stat label="เปิดลิงก์เฉลี่ย / ส่งหลักฐานเฉลี่ย" value={`${duration(b.avgOpenSec)} / ${duration(b.avgProofSec)}`} /></Grid>
          <Grid item xs={6} sm={3}><Stat label="ยอดกด" value={yen(b.jpy || 0)} /></Grid>
        </Grid>
        {error && <Alert severity="error">{error}</Alert>}
        {!data && !error && <Box textAlign="center" py={3}><CircularProgress size={24} /></Box>}
        {data && (
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>ส่งการ์ดเมื่อ</TableCell>
                  <TableCell>สินค้า</TableCell>
                  <TableCell align="right">ราคา</TableCell>
                  <Head title="เปิดลิงก์ใน" hint="จากส่งการ์ด ถึงเปิดลิงก์ครั้งแรก" />
                  <Head title="ส่งหลักฐานใน" hint="จากส่งการ์ด ถึงส่งหลักฐาน" />
                  <TableCell>ผล</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {data.cards.length === 0 && (
                  <TableRow><TableCell colSpan={6}><Typography variant="body2" color="text.secondary">ไม่มีการ์ดในช่วงนี้</Typography></TableCell></TableRow>
                )}
                {data.cards.map((c) => (
                  <TableRow key={c.offerId}>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>{whenThai(c.sentAt)}</TableCell>
                    <TableCell>
                      {c.url ? <Link href={c.url} target="_blank" rel="noopener noreferrer">{c.url.replace(/^https?:\/\/(jp\.)?/, '')}</Link> : '—'}
                      {c.orderCode && <Typography variant="caption" color="text.secondary" display="block">{c.orderCode}</Typography>}
                    </TableCell>
                    <TableCell align="right">{yen(c.priceJpy)}</TableCell>
                    <TableCell align="right">{duration(c.openSec)}</TableCell>
                    <TableCell align="right">{duration(c.proofSec)}</TableCell>
                    <TableCell><Chip size="small" color={OUTCOME[c.outcome]?.color || 'default'} label={OUTCOME[c.outcome]?.label || c.outcome} /></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </DialogContent>
      <DialogActions><Button onClick={onClose}>ปิด</Button></DialogActions>
    </Dialog>
  );
};

const money = (b) => `${b.balance < 0 ? '-' : ''}${b.currency}${Math.abs(b.balance).toLocaleString(undefined, { maximumFractionDigits: 2 })}`;
const BALANCE_NOTE = {
  none: 'ชีตไม่มีช่องยอดคงเหลือ',
  unreadable: 'ช่องยอดคงเหลือในชีตอ่านไม่ได้',
  error: 'เปิดชีตไม่ได้',
};

/** Money each buyer still holds, read from their Google Sheet: latest, not tied to the date range. */
const BalanceSection = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const load = useCallback(async (refresh = false) => {
    setLoading(true);
    setError('');
    try {
      const res = await DispatchService.getBalances(refresh);
      setData(res.data);
    } catch (err) {
      setError(err?.response?.data?.message || 'อ่าน Google Sheet ไม่สำเร็จ');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  return (
    <Box mb={2}>
      <Box display="flex" alignItems="center" gap={1} mb={1} flexWrap="wrap">
        <Typography variant="subtitle1" fontWeight={700}>ยอดเงินคงเหลือ</Typography>
        <Typography variant="caption" color="text.secondary">
          จาก Google Sheet ของแต่ละคน ยอดล่าสุด ไม่ขึ้นกับช่วงวันที่{data ? ` · อ่านเมื่อ ${thaiTime(data.readAt)}` : ''}
        </Typography>
        <Box flexGrow={1} />
        <Button size="small" disabled={loading} onClick={() => load(true)}>{loading ? 'กำลังอ่าน...' : 'อ่านใหม่'}</Button>
      </Box>
      {error && <Alert severity="error">{error}</Alert>}
      <Grid container spacing={2}>
        {(data?.buyers || []).map((b) => (
          <Grid item xs={6} sm={4} xl={2} key={b.id}>
            {b.status === 'ok' ? (
              <Stat label={b.label} value={money(b)} color={b.balance < 0 ? 'error.main' : undefined} sub={b.balance < 0 ? 'ติดลบ ต้องโอนเพิ่ม' : null} />
            ) : (
              <Stat label={b.label} value="—" sub={BALANCE_NOTE[b.status]} />
            )}
          </Grid>
        ))}
      </Grid>
    </Box>
  );
};

export default function DispatchReport() {
  const theme = useTheme();
  const shopName = useSelector((state) => state.shop.name);
  const [range, setRange] = useState(() => rangeOf(RANGES[2]));
  const [picked, setPicked] = useState(null); // one day of the range, or null for all of it
  const [detailOf, setDetailOf] = useState(null); // the buyer whose popup is open
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Only the latest range's answer counts: a slower earlier request (fast clicks, typing a date,
  // switching shop) is ignored when it arrives. On an error the old numbers go, so nothing stale shows.
  useEffect(() => {
    let live = true;
    setLoading(true);
    setError('');
    DispatchService.getReport({ ...range, shop: shopName || null })
      .then((res) => { if (live) { setData(res.data); setPicked(null); } })
      .catch((err) => { if (live) { setData(null); setError(err?.response?.data?.message || 'โหลดข้อมูลไม่สำเร็จ'); } })
      .finally(() => { if (live) setLoading(false); });
    return () => { live = false; };
  }, [range, shopName]);

  const days = useMemo(() => data?.days || [], [data]);
  const day = days.find((d) => d.date === picked) || null;
  const summary = data?.summary;
  const settledJobs = summary ? summary.done + summary.soldOut : 0;

  const chart = useMemo(() => ({
    labels: days.map((d) => dayLabel(d.date)),
    datasets: [{
      label: 'ลิงก์ที่กดสำเร็จ',
      data: days.map((d) => d.done),
      backgroundColor: days.map((d) => (!picked || d.date === picked ? theme.palette.primary.main : theme.palette.action.disabled)),
      borderRadius: 4,
      maxBarThickness: 28,
    }],
  }), [days, picked, theme]);

  const chartOptions = useMemo(() => ({
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      tooltip: {
        callbacks: {
          afterLabel: (ctx) => {
            const d = days[ctx.dataIndex];
            return `ออเดอร์ ${d.jobs} · ซื้อไม่ทัน ${d.soldOut} · ยอด ${yen(d.jpy)}`;
          },
        },
      },
    },
    scales: {
      x: { grid: { display: false }, ticks: { color: theme.palette.text.secondary } },
      y: { beginAtZero: true, grid: { color: theme.palette.divider }, ticks: { precision: 0, color: theme.palette.text.secondary } },
    },
    onClick: (_e, elements) => {
      if (elements.length === 0) return;
      const date = days[elements[0].index].date;
      setPicked((cur) => (cur === date ? null : date));
    },
  }), [days, theme]);

  return (
    <Box p={2}>
      <Typography variant="h5" fontWeight={700} mb={2}>สรุปคนกด Mercari</Typography>

      {/* Section 1 — balances: the latest figure from each buyer's sheet, not tied to the date range. */}
      <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
        <BalanceSection />
      </Paper>

      {/* Section 2 — the bot's work over the chosen range. */}
      <Paper variant="outlined" sx={{ p: 2 }}>
      <Box display="flex" alignItems="center" gap={2} flexWrap="wrap" mb={2}>
        <Typography variant="subtitle1" fontWeight={700}>สรุปการกด</Typography>
        <Box flexGrow={1} />
        <ButtonGroup size="small" variant="outlined">
          {RANGES.map((r) => {
            const rr = rangeOf(r);
            const on = rr.from === range.from && rr.to === range.to;
            return <Button key={r.label} variant={on ? 'contained' : 'outlined'} onClick={() => setRange(rr)}>{r.label}</Button>;
          })}
        </ButtonGroup>
        <TextField
          size="small" type="date" label="ตั้งแต่" InputLabelProps={{ shrink: true }}
          inputProps={{ max: range.to, min: ymd(addDays(parseISO(range.to), -(MAX_DAYS - 1))) }}
          value={range.from} onChange={(e) => e.target.value && e.target.value <= range.to && setRange((r) => ({ ...r, from: e.target.value }))}
        />
        <TextField
          size="small" type="date" label="ถึง" InputLabelProps={{ shrink: true }}
          inputProps={{ min: range.from, max: ymd(addDays(parseISO(range.from), MAX_DAYS - 1)) }}
          value={range.to} onChange={(e) => e.target.value && e.target.value >= range.from && setRange((r) => ({ ...r, to: e.target.value }))}
        />
        {loading && <CircularProgress size={20} />}
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {summary && (
        <>
          <Grid container spacing={2} mb={2}>
            <Grid item xs={6} sm={4} xl={2}><Stat label="ออเดอร์เข้าบอท" value={summary.jobs.toLocaleString()} sub={summary.live > 0 ? `ยังไม่จบ ${summary.live}` : null} /></Grid>
            <Grid item xs={6} sm={4} xl={2}><Stat label="กดสำเร็จ" value={`${summary.done.toLocaleString()} ลิงก์`} sub={settledJobs ? `${Math.round((summary.done / settledJobs) * 100)}% ของงานที่จบ` : null} /></Grid>
            <Grid item xs={6} sm={4} xl={2}><Stat label="ยอดรวมที่กด" value={yen(summary.jpy)} /></Grid>
            <Grid item xs={6} sm={4} xl={2}><Stat label="ซื้อไม่ทัน" value={summary.soldOut.toLocaleString()} sub={summary.adminTaken > 0 ? `แอดมินซื้อเอง ${summary.adminTaken}` : null} /></Grid>
            <Grid item xs={6} sm={4} xl={2}><Stat label="เปิดลิงก์เฉลี่ย" value={duration(summary.avgOpenSec)} /></Grid>
            <Grid item xs={6} sm={4} xl={2}><Stat label="ส่งหลักฐานเฉลี่ย" value={duration(summary.avgProofSec)} sub="อาจนานกว่าเวลากดจริง" /></Grid>
          </Grid>

          {days.length > 1 && (
            <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
              <Typography variant="subtitle1" fontWeight={700}>ลิงก์ที่กดสำเร็จต่อวัน</Typography>
              <Typography variant="caption" color="text.secondary">กดแท่งของวันไหน ตารางคนกดจะแสดงเฉพาะวันนั้น</Typography>
              <Box height={220} mt={1}><Bar data={chart} options={chartOptions} /></Box>
            </Paper>
          )}

          <Box display="flex" alignItems="center" gap={1} mb={1}>
            <Typography variant="subtitle1" fontWeight={700}>แยกตามคนกด</Typography>
            {day && <Chip size="small" color="primary" label={dayLabel(day.date)} onDelete={() => setPicked(null)} />}
          </Box>
          <BuyerTable buyers={day ? day.buyers : data.buyers} onPick={setDetailOf} />
          <Typography variant="caption" color="text.secondary">กดชื่อคนกด ดูรายละเอียดทุกลิงก์</Typography>
          {detailOf && (
            <BuyerDetail
              buyer={detailOf}
              range={day ? { from: day.date, to: day.date } : range}
              shop={shopName || null}
              onClose={() => setDetailOf(null)}
            />
          )}

          {days.length > 1 && (
            <>
              <Typography variant="subtitle1" fontWeight={700} mt={3} mb={1}>แยกตามวัน</Typography>
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>วันที่</TableCell>
                      <TableCell align="right">ออเดอร์</TableCell>
                      <TableCell align="right">กดสำเร็จ</TableCell>
                      <TableCell align="right">ซื้อไม่ทัน</TableCell>
                      <TableCell align="right">แอดมินซื้อเอง</TableCell>
                      <TableCell align="right">ยกเลิก</TableCell>
                      <TableCell align="right">ยังไม่จบ</TableCell>
                      <TableCell align="right">ยอดรวม</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {[...days].reverse().map((d) => (
                      <TableRow
                        key={d.date} hover selected={d.date === picked} sx={{ cursor: 'pointer' }}
                        onClick={() => setPicked((cur) => (cur === d.date ? null : d.date))}
                      >
                        <TableCell>{dayLabel(d.date)}</TableCell>
                        <TableCell align="right">{d.jobs}</TableCell>
                        <TableCell align="right">{d.done}</TableCell>
                        <TableCell align="right">{d.soldOut}</TableCell>
                        <TableCell align="right">{d.adminTaken}</TableCell>
                        <TableCell align="right">{d.cancelled}</TableCell>
                        <TableCell align="right">{d.live}</TableCell>
                        <TableCell align="right">{yen(d.jpy)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </>
          )}
          <Typography variant="caption" color="text.secondary" display="block" mt={1}>
            นับตามวันที่คีย์ออเดอร์ วันหนึ่งเริ่ม 02:00 ถึง 02:00 ของอีกวัน ยอดรวมนับเฉพาะลิงก์ที่กดสำเร็จ เป็นราคาสินค้าเงินเยน
          </Typography>
        </>
      )}
      </Paper>
    </Box>
  );
}
